import { createApp, h } from "vue";
import { ref } from "vue";
import {
    Dialog as HDialog, DialogOverlay as HDialogOverlay, DialogTitle as HDialogTitle, DialogDescription as HDialogDescription,
    Disclosure, DisclosureButton, DisclosurePanel,
    Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions,
    Menu as HMenu, MenuButton as HMenuButton, MenuItems as HMenuItems, MenuItem as HMenuItem,
    Popover, PopoverButton, PopoverGroup, PopoverPanel,
    RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption,
    Switch as HSwitch, SwitchGroup as HSwitchGroup, SwitchLabel as HSwitchLabel,
    TabGroup, TabList, Tab, TabPanels, TabPanel,
    TransitionRoot, TransitionChild,
    } from "@headlessui/vue"

import axios from 'axios'
import Chart from 'chart.js'
import { flare } from '@flareapp/flare-client';
import { flareVue } from '@flareapp/flare-vue';
import GphApiClient from 'giphy-js-sdk-core'
import heic2any from 'heic2any'
import LitepieDatepicker from 'litepie-datepicker-tw3';
import moment from 'moment'
import numeral from 'numeral'
import { QuillEditor } from '@vueup/vue-quill'
import RecordRTC from 'recordrtc'
import { Carousel as VCCarousel, Slide as VCSlide, Pagination as VCPagination, Navigation as VCNavigation } from 'vue3-carousel'

import '@vueup/vue-quill/dist/vue-quill.bubble.css'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import 'vue3-carousel/dist/carousel.css'

// axios config
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.headers.common['X-CSRF-TOKEN'] = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;

// flare config
if (process.env.NODE_ENV === 'production') {
    flare.light();
}

// moment config
moment.updateLocale('en', {
    week: {
        dow: 1    // set first day of week to Monday
    }
});

// enable spell checker in quill
import Quill from 'quill'
const Inline = Quill.import('blots/inline');
class CustomColor extends Inline {
  constructor(domNode, value) {
    super(domNode, value);

    // Map <font> properties
    domNode.style.color = domNode.color;

    const span = this.replaceWith(new Inline(Inline.create()));

    span.children.forEach(child => {
      if (child.attributes) child.attributes.copy(span);
      if (child.unwrap) child.unwrap();
    });

    this.remove();

    return span;
  }
}

CustomColor.blotName = "customColor";
CustomColor.tagName = "FONT";
Quill.register(CustomColor, true);

let quillOptions = {
    contentType: 'QuillEditor',
    modules: {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
            [{ 'align': [] }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link', 'image', 'video'],
            ['clean'],
        ]
    },
    placeholder: 'Start typing here...',
    theme: 'bubble',
    scrollingContainer: 'html',
};
let quillOptionsNoFiles = {
    contentType: 'QuillEditor',
    modules: {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
            [{ 'align': [] }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link'],
            ['clean'],
        ]
    },
    placeholder: 'Start typing here...',
    theme: 'bubble',
    scrollingContainer: 'html',
};
QuillEditor.props.globalOptions.default = () => quillOptions;

// create app
const app = createApp({
    mixins: [appMixins, adminMixins],
    components: {
    },
    data() {
    	return {
	        vueAppLoading: true,
	        Chart: Chart,
            giphy: GphApiClient('wD3vtsmjViV020rB6fXEEeNXzh03fc3R'),
            heic2any: heic2any,
	        moment: moment,
	        numeral: numeral,
	        RecordRTC: RecordRTC,
            quillOptionsNoFiles: quillOptionsNoFiles,

            darkMode: 0,
            bottomFloatOff: false,
	        bottomNavOn: false,
            poweredByPonoFixedOn: true,
	        overlayOn: false,
            overlayBottomOn: false,
	        scrollDownOn: true,
	        treeExpanded: false,
	        formTouched: false,
	        adminMenuOn: true,
	        isHovered: [],

            modalOpen: [],

            modalOkOpen: false,                  // ok modal
            modalOkTitle: '',
            modalOkContent: '',
            modalOkBtn: '',

            modalWarningOpen: false,             // warning modal
            modalWarningTitle: '',
            modalWarningContent: '',
            modalWarningBtn: '',

            modalConfirmOpen: false,             // confirm modal
            modalConfirmFn: '',
            modalConfirmId1: null,
            modalConfirmId2: null,
            modalConfirmTitle: '',
            modalConfirmContent: '',
            modalConfirmBtn1: '',
            modalConfirmBtn2: '',

            modalNotificationsOpen: false,
            modalNotificationDetailsOpen: false,
            modalNotificationDetailsData: null,
            modalPlaceholdersOpen: false,

            modalAsset1Src: '',                  // asset modals
            modalAsset1Name: '',                 // asset modals
            modalAsset1Rotation: 0,
            modalAsset1Open: false,
            modalAsset11Src: '',
            modalAsset11Open: false,
            modalAsset13Converting: false,
            modalAsset13Src: '',
            modalAsset13Open: false,
            modalAsset14Src: '',
            modalAsset14Open: false,

            passwordInputVisible: true,            // show/hide password in all password input boxes

            toastApp: '',                          // toast notifications
            toastAdmin: '',

	        notifications: null,
	        notificationsRecent: null,
            notificationTypes: null,
            notificationTypeFilter: null,
	        keepInTouchCount: 0,
            keepInTouchOverdueCount: 0,
	        unreadNotificationCount: vunreadNotificationCount,
            visibleNotificationCount: 50,
	        recentNotificationCount: vrecentNotificationCount,

	        alertCountDown: 0,                                             // seconds before dismissing b-alert

	        acceptBox: null,
	        signatureBox: null,

	        file_upload: null,
	        main_asset: 0,
	        preview_asset: 0,
	        remove_assets: '',
	        uploadAssets: [],
            uploadAssetsCount: 0,
            uploadAssetsRemoved: [],
	        uploadYoutubes: [],
	        addYoutube: '',
	        uploadVimeos: [],
	        addVimeo: '',
            uploadWistias: [],
            addWistia: '',

            scrollY: 0,

            userNotesClicked: [],                        // admin has clicked on a note to expand it
            userNotesDismissed: [],                      // highligted note has been dismissed by admin
            userNotesHidden: false,

            productOptionTrackInventory: true,
            productOptionInventory: 0,
            productOptionInventoryLoaded: false,
	    }
    },
    methods:
    {
        // blur focus from all inputs
        blurFocus()
        {
            setTimeout(() => {
                let el = document.querySelector(':focus');
                if (el)
                {
                    el.blur();
                }
            }, 200);
        },

        // toggle dark mode
        toggleDarkMode(value)
        {
            this.darkMode = value;

            if (this.darkMode == 1)
            {
                localStorage.ponoTheme = 'light';
                document.documentElement.classList.remove('dark');
            }
            else if (this.darkMode == 2)
            {
                localStorage.ponoTheme = 'dark';
                document.documentElement.classList.add('dark');
            }
            else
            {
                localStorage.ponoTheme = 'auto';
                document.documentElement.classList.remove('dark');
            }
        },

        // handle scroll events
        scrollListener()
        {
            // show/hide powered by Pono on scroll
            if (window.scrollY > 100)
            {
                this.poweredByPonoFixedOn = false;
            }
            else
            {
                this.poweredByPonoFixedOn = true;
            }

            // show/hide bottom nav links for scroll to top and contact
            if (window.scrollY > 400)
            {
                this.bottomNavOn = true;
                this.scrollDownOn = false;
            }
            else
            {
                this.bottomNavOn = false;
                this.scrollDownOn = true;
            }
        },

        // scroll down
        scrollDown()
        {
            window.scrollTo(0, window.innerHeight);
        },

        // scroll to bottom
        scrollToBottom()
        {
            window.scrollTo(0, 9999999);
        },

        // scroll to top
        scrollToTop()
        {
            window.scrollTo(0, 0);
        },

        // reload page
        reloadPage()
        {
            this.showOverlay();
            window.location.reload();
        },

        // reload page without triggering any POST to repear
        reloadPageViaRedirect()
        {
            this.showOverlay();
            window.location.href = window.location.href;
        },

        // viewing on mobile device
        isMobile()
        {
            return window.innerWidth < 768 ? true : false;
        },

        // check if is numeric
        isNumeric(str) {
            return !isNaN(str) && !isNaN(parseFloat(str));
        },

        // show page overlay
    	showOverlay()
    	{
            this.overlayOn = true;
    	},

        // hide page overlay
        hideOverlay()
        {
            this.overlayOn = false;
        },

        // show top overlay
        showBottomOverlay()
        {
            this.overlayBottomOn = true;
        },

        // hide top overlay
        hideBottomOverlay()
        {
            this.overlayBottomOn = false;
        },

        // auto-dismissing alert
        alertCountdownChanged(alertCountdown)
        {
            this.alertCountdown = alertCountdown;
        },

        adminMenuShowHide()
        {
            this.adminMenuOn = !this.adminMenuOn;
        },

        // open link
        openLink(e, link, action=0, stopPropagation=false)
        {
            // stop propagation if pressing shift key
            if (e.shiftKey)
            {
                e.stopPropagation();
                return;
            }

            // stop propagation
            if (stopPropagation)
            {
                e.stopPropagation();
            }

            if (!link)
            {
                return;
            }

            if (link.toString().substr(0,4) == 'www.')
            {
                link = 'http://' + link;
            }

            if (action == 2 || (link.toString().substr(0,4) == 'http' && action !== 1 && !link.includes(window.location.origin)))
            {
                window.open(link);
            }
            else
            {
                if (e.ctrlKey || e.metaKey)
                {
                    window.open(link);
                }
                else
                {
                    location.href = link;
                }
            }
        },

        // open link with overlay
        openLinkWithOverlay(e, link, action=0, stopPropagation=false)
        {
            if (!event.ctrlKey && !event.shiftKey && !event.metaKey)
            {
                this.showOverlay();
            }

            setTimeout(() => { this.openLink(e, link, action, stopPropagation); }, 100);
            setTimeout(() => { this.hideOverlay(); }, 3000);    // handle browser nav with back button
        },

        openUrl(e, url)
        {
            e.stopPropagation();
            window.open(url);
        },

        // copy to clipboard
        copyToClipboard(content, message, toastTimeout=5000)
        {
            let textArea = document.createElement('textarea');
            textArea.value = content;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);

            this.showToastApp(message ? message : 'Copied to the clipboard', toastTimeout);
            this.showToastAdmin(message ? message : 'Copied to the clipboard');

            // remove selection
            if (window.getSelection)
            {
                window.getSelection().removeAllRanges();
            }
            else if (document.selection)
            {
                document.selection.empty();
            }
        },

        // clicked on admin user note to expand/collapse it
        clickOnNote(id) {
            let index = this.userNotesClicked.indexOf(id);
            if (index > -1)
            {
                this.userNotesClicked.splice(index, 1);
            }
            else
            {
                this.userNotesClicked.push(id);
            }
        },

        // set datepicker to today
        dateSetToday(vmodel, range)
        {
            if (range)
            {
                this[vmodel] = this.moment().format('DD MMM YYYY') + ' ~ ' + this.moment().format('DD MMM YYYY');
                this.$refs[vmodel].pickerValue = this.moment().format('DD MMM YYYY') + ' ~ ' + this.moment().format('DD MMM YYYY');
            }
            else
            {
                this[vmodel] = this.moment().format('DD MMM YYYY');
                this.$refs[vmodel].pickerValue = this.moment().format('DD MMM YYYY');
            }

            this.touchForm();
        },

        // mark admin notification as read
        markNotificationAsRead(notification)
        {
            this.axios.post('/ponocms/notifications/'+notification.id+'/mark-as-read').then(res => {
                if (res && res.data && res.data.success)
                {
                    notification.read_at = new Date();
                    this.getNotificationCount();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // mark admin notification as unread
        markNotificationAsUnread(notification)
        {
            this.axios.post('/ponocms/notifications/'+notification.id+'/mark-as-unread').then(res => {
                if (res && res.data && res.data.success)
                {
                    notification.read_at = null;
                    this.getNotificationCount();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // mark all admin notification as read
        markAllNotificationAsRead(id)
        {
            this.openModalConfirm('Are you sure?',
                                    'Would you like to mark all notifications as read?',
                                    'Yes, mark all as read now',
                                    'No, do not mark as read',
                                    'markAllNotificationAsReadConfirmed',
                                    id);
        },

        markAllNotificationAsReadConfirmed(id)
        {
            this.showOverlay();
            window.location.href = '/ponocms/users/' + id + '/notifications/mark-all-as-read';
        },

        // view admin notification detail popup
        viewNotificationDetails(notification)
        {
            this.modalNotificationDetailsData = notification;
            this.modalNotificationDetailsOpen = true;
        },

        // update unread notification count
        getNotificationCount()
        {
            this.axios.post('/ponocms/load-unread-notifications').then(res => {
                if (res && res.data && res.data.success)
                {
                    this.unreadNotificationCount = res.data.unreadNotificationCount;
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // called (mostly from admin) when any form element is touched
        touchForm()
        {
            this.formTouched = true;
        },

        // submit form
        submitForm(ref='form')
        {
            // copy quill content
            try
            {
                this.submitFormAdditional();
            }
            catch(e) {}

            this.showOverlay();

            setTimeout(() => { this.$refs[ref].submit(); }, 100);
        },

        // submit form as draft
        submitFormAsDraft()
        {
            this.showOverlay();
            setTimeout(() => {
                document.getElementById('draft_save_for_later').value = 1;
                this.$refs.form.submit();
            }, 100);
        },

        // expand/collapse tree
        expandCollapseTree()
        {
            this.treeExpanded = !this.treeExpanded;
        },

        // handle hover
        handleHover(hovered, id)
        {
            this.isHovered[id] = hovered ? 1 : 0;
            this.$forceUpdate();
        },

        // open OK modal
        openModalOk(title, content, btn='')
        {
            this.modalOkTitle = title;
            this.modalOkContent = content;
            this.modalOkBtn = btn;
            this.modalOkOpen = true;
            this.$forceUpdate();
        },

        // close OK modal
        closeModalOk()
        {
            this.modalOkOpen = false;
            this.$forceUpdate();
        },

        // open warning modal
        openModalWarning(title, content, btn='')
        {
            this.modalWarningTitle = title;
            this.modalWarningContent = content;
            this.modalWarningBtn = btn;
            this.modalWarningOpen = true;
            this.$forceUpdate();
        },

        // close warning modal
        closeModalWarning()
        {
            this.modalWarningOpen = false;
            this.$forceUpdate();
        },

        // open confirm modal
        openModalConfirm(title, content, btn1='', btn2='', fn='', id1=null, id2=null)
        {
            this.modalConfirmTitle = title;
            this.modalConfirmContent = content;
            this.modalConfirmBtn1 = btn1;
            this.modalConfirmBtn2 = btn2;
            this.modalConfirmFn = fn;
            this.modalConfirmId1 = id1;
            this.modalConfirmId2 = id2;
            this.modalConfirmOpen = true;
        },

        // close OK modal
        closeModalConfirm(confirmed=false)
        {
            this.modalConfirmOpen = false;

            if (confirmed && this.modalConfirmFn)
            {
                if (this.modalConfirmId1 && this.modalConfirmId2)
                {
                    this[this.modalConfirmFn](this.modalConfirmId1, this.modalConfirmId2);
                }
                else if (this.modalConfirmId1)
                {
                    this[this.modalConfirmFn](this.modalConfirmId1);
                }
                else
                {
                    this[this.modalConfirmFn]();
                }
            }
        },

        // open notifications modal
        openModalNotifications(filter='', removeFilter=false)
        {
            this.notificationTypeFilter = filter;

            this.axios.post('/ponocms/load-unread-notifications?' + (filter ? 'notification='+filter+'&' : '') + (removeFilter ? 'clearfilter=1' : '')).then(res => {
                if (res && res.data && res.data.success)
                {
                    this.notifications = res.data.notifications;
                    this.notificationsRecent = res.data.recents;
                    this.notificationTypes = res.data.notificationTypes;
                    this.unreadNotificationCount = res.data.unreadNotificationCount;
                    this.recentNotificationCount = res.data.recentNotificationCount;
                    this.notificationTypeFilter = res.data.notificationFilter;

                    this.modalNotificationsOpen = true;
                    this.$forceUpdate();
                    this.blurFocus();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // load previous or next page of notifications
        loadPreviousNextModalNotifications(previous=false)
        {
            this.showOverlay();
            this.modalNotificationsOpen = false;
            if (previous)
            {
                this.visibleNotificationCount -= 100;
            }

            this.axios.post('/ponocms/load-unread-notifications?' + (this.notificationTypeFilter ? 'notification='+this.notificationTypeFilter+'&' : '') + ('from='+this.visibleNotificationCount)).then(res => {
                this.hideOverlay();

                if (res && res.data && res.data.success)
                {
                    this.visibleNotificationCount += 50;

                    this.notifications = res.data.notifications;
                    this.notificationsRecent = res.data.recents;
                    this.notificationTypes = res.data.notificationTypes;
                    this.unreadNotificationCount = res.data.unreadNotificationCount;
                    this.recentNotificationCount = res.data.recentNotificationCount;
                    this.modalNotificationsOpen = true;
                    this.$forceUpdate();
                    this.blurFocus();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // close notifications modal
        closeModalNotifications()
        {
            this.modalNotificationDetailsOpen = false;
            this.modalNotificationsOpen = false;
            this.$forceUpdate();
        },

        // close notifications modal
        closeModalNotificationDetails()
        {
            this.modalNotificationDetailsOpen = false;
            this.$forceUpdate();
        },

        // open PONO placeholders modal
        openModalPlaceholders()
        {
            this.modalPlaceholdersOpen = true;
            this.$forceUpdate();
        },

        // close PONO placeholders modal
        closeModalPlaceholders()
        {
            this.modalPlaceholdersOpen = false;
            this.$forceUpdate();
        },

        // show modal for assets type 1, images
        openModalAsset1(src, name='', rotation=0)
        {
            this.modalAsset1Src = src;
            this.modalAsset1Name = name;
            this.modalAsset1Rotation = rotation;
            this.modalAsset1Open = true;
            this.$forceUpdate();
        },

        // close asset 1 modal
        closeModalAsset1()
        {
            this.modalAsset1Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 11, youtube videos
        openModalAsset11(src)
        {
            this.modalAsset11Src = src;
            this.modalAsset11Open = true;
            this.$forceUpdate();
        },

        // close asset 11 modal
        closeModalAsset11()
        {
            this.modalAsset11Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 13, website videos
        openModalAsset13(src, converting)
        {
            this.modalAsset13Converting = converting;
            this.modalAsset13Src = src;
            this.modalAsset13Open = true;
            this.$forceUpdate();
        },

        // close asset 13 modal
        closeModalAsset13()
        {
            this.modalAsset13Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 14, vimeo videos
        openModalAsset14(src)
        {
            this.modalAsset14Src = src;
            this.modalAsset14Open = true;
            this.$forceUpdate();
        },

        // close asset 14 modal
        closeModalAsset14()
        {
            this.modalAsset14Open = false;
            this.$forceUpdate();
        },

        // open view specific modal
        openModal(name)
        {
            this.modalOpen[name] = true;
            this.$forceUpdate();
        },

        // close view specific modal
        closeModal(name)
        {
            this.modalOpen[name] = false;
            this.$forceUpdate();
        },

        // fix to open Litepie datepicker in modal
        openDatepicker(name)
        {
            this.$refs[name].isShow = !this.$refs[name].isShow;
        },

        // show toast app
        showToastApp(str, timeout=5000)
        {
            this.toastApp = str;

            setTimeout(() => { this.toastApp = ''; }, timeout);
        },

        // show toast admin
        showToastAdmin(str)
        {
            this.toastAdmin = str;

            setTimeout(() => { this.toastAdmin = ''; }, 5000);
        },

        // make preview asset
        previewAsset(id)
        {
            this.preview_asset = id;
            this.touchForm();
        },

        // remove assets
        removeAsset(id)
        {
            this.remove_assets = this.remove_assets + id + ',';
            this.touchForm();
        },

        heicToJpg(url,id,overlay=false)
        {
            if (overlay)
            {
                this.showOverlay();
            }
            else
            {
                this.showBottomOverlay();
            }

            try
            {
                fetch(url)
                    .then((res) => res.blob())
                    .then((blob) =>
                        heic2any(
                            {
                                blob,
                                toType: "image/jpeg",
                                quality: 0.5,
                            }
                        )
                    )
                    .then((conversionResult) => {
                        var t = URL.createObjectURL(conversionResult);
                        document.getElementById(id).src = t;

                        if (overlay)
                        {
                            this.hideOverlay();
                        }
                        this.hideBottomOverlay();
                    })
                    .catch((e) => {
                        if (overlay)
                        {
                            this.hideOverlay();
                        }
                        this.hideBottomOverlay();
                    });
            }
            catch (e)
            {
                if (overlay)
                {
                    this.hideOverlay();
                }
                this.hideBottomOverlay();
            }
        },

        // choose file for upload by clicking on file_upload input
        fileUploadChoose(n)
        {
            for (let i=1; i<=20; i++)
            {
                if (!this['file_upload'+n+i])
                {
                    document.getElementById('file_upload'+n+i).click();

                    return;
                }
            }
        },

        // add chosen file from file_upload input for upload
        fileUploadChooseAdd(e,n)
        {
            this.touchForm();

            for (let i=1; i<=20; i++)
            {
                // prevent adding double, as on some browsers when dragging/dropping file will have already been added in fileUploadDrop and fileUploadChooseAdd will additionally be triggered
                if (this.uploadAssets[n] && this.uploadAssets[n].includes(e.target.files))
                {
                    return;
                }

                else if (!this['file_upload'+n+i])
                {
                    if (!this.uploadAssets[n])
                    {
                        this.uploadAssets[n] = [];
                    }
                    this.uploadAssets[n].push(e.target.files);

                    this['file_upload'+n+i] = e.target.files;

                    this.uploadAssetsCount += 1;

                    return;
                }
            }
        },

        // drop file for upload
        fileUploadDrop(e,n,accept)
        {
            // validate file types
            if (accept)
            {
                for (let file of e.dataTransfer.files)
                {
                    if (file)
                    {
                        if (accept == 'image/*' && !file.type.includes('image/'))
                        {
                            this.openModalWarning('ERROR','This type of file is not allowed. Please only upload images (for example, .jpg or .png files).');
                            return;
                        }
                        else if (accept == 'application/pdf' && !file.type.includes('pdf'))
                        {
                            this.openModalWarning('ERROR','This type of file is not allowed. Please only upload PDFs.');
                            return;
                        }
                        else if (accept == 'text/csv' && !file.type.includes('text') && !file.type.includes('csv'))
                        {
                            this.openModalWarning('ERROR','This type of file is not allowed. Please only upload text/csv files.');
                            return;
                        }
                    }
                }
            }

            this.touchForm();

            for (let i=1; i<=20; i++)
            {
                if (!this['file_upload'+n+i])
                {
                    if (!this.uploadAssets[n])
                    {
                        this.uploadAssets[n] = [];
                    }
                    this.uploadAssets[n].push(e.dataTransfer.files);

                    this['file_upload'+n+i] = e.dataTransfer.files;
                    this.$refs['file_upload'+n+i].files = e.dataTransfer.files;

                    return;
                }
            }
        },

        // remove previously chosen or dropped file from upload
        fileUploadRemove(e,n,file)
        {
            for (let i=1; i<=20; i++)
            {
                if (this['file_upload'+n+i])
                {
                    let pos = 0;
                    for (let item of this['file_upload'+n+i])
                    {
                        if (item && item.name == file.name && item.size == file.size)
                        {
                            this.uploadAssetsRemoved.push(n+'-'+i+'-'+pos);

                            this.uploadAssetsCount -= 1;

                            this.$forceUpdate();
                        }

                        pos++;
                    }
                }

            }
        },

        // format a URL that is being inputted in a form
        formatURLinput(vmodel)
        {
            let url = this[vmodel].toLowerCase().replace(/[^A-Za-z0-9-\s]/g,'');
            this[vmodel] = url.trim().replace(/ /g, '-');
        },

        // format youtube link
        formatYoutubeLink()
        {
            let link = this.addYoutube;
            if (!link)
            {
                return;
            }

            this.addYoutube = '';

            if (link.includes('youtube') || link.includes('youtu.be'))
            {
                let url = link.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
                link = url[2] !== undefined ? url[2].split(/[^0-9a-z_\-]/i)[0] : url[0];
            }

            this.uploadYoutubes.push(link);
        },

        // remove just added youtube video
        removeYoutube(link)
        {
            let index = this.uploadYoutubes.indexOf(link);
            if (index > -1)
            {
                this.uploadYoutubes.splice(index, 1);
            }
        },

        // format vimeo link
        formatVimeoLink()
        {
            let link = this.addVimeo;
            if (!link)
            {
                return;
            }

            this.addVimeo = '';

            if (link.includes('vimeo'))
            {
                let url = /vimeo.*\/(\d+)/i.exec(link);
                link = url ? url[1] : '';
            }

            this.uploadVimeos.push(link);
        },

        // remove just added vimeo video
        removeVimeo(link)
        {
            let index = this.uploadVimeos.indexOf(link);
            if (index > -1)
            {
                this.uploadVimeos.splice(index, 1);
            }
        },

        // format wistia link
        formatWistiaLink()
        {
            let link = this.addWistia;
            if (!link)
            {
                return;
            }

            this.addWistia = '';

            let t = /[^/]*$/.exec(link);
            if (t.length > 0)
            {
                link = t[0];
            }

            this.uploadWistias.push(link);
        },

        // remove just added wistia video
        removeWistia(link)
        {
            let index = this.uploadWistias.indexOf(link);
            if (index > -1)
            {
                this.uploadWistias.splice(index, 1);
            }
        },

        // update quill editor box content when saving
        // mp++ this needs to be implemented for all quill boxes
        updateQuill(id, target)
        {
            if (target.includes('.'))
            {
                let t = target.split('.');
                let t1 = document.getElementById(id);
                if (t1)
                {
                    this[t[0]][t[1]] = t1.querySelector('.ql-editor').innerHTML;

                    if (this[t[0]][t[1]] == '<p><br></p>')
                    {
                        this[t[0]][t[1]] = null;
                    }
                }
            }
            else
            {
                let t = document.getElementById(id);
                if (t)
                {
                    this[target] = t.querySelector('.ql-editor').innerHTML;

                    if (this[target] == '<p><br></p>')
                    {
                        this[target] = null;
                    }
                }
            }
        },

        // increase value of +/- button in a form/journal field
        increasePlusMinusValue(name, min=0, max=0)
        {
            this.touchForm();

            if (!this.pForm[name] || isNaN(this.pForm[name]))
            {
                this.pForm[name] = parseInt(0);
            }

            if (max == 0 || parseInt(this.pForm[name]) < max)
            {
                this.pForm[name] = parseInt(this.pForm[name]) + 1;
            }
        },

        // decrease value of +/- button in a form/journal field
        decreasePlusMinusValue(name, min=0, max=0)
        {
            this.touchForm();

            if (!this.pForm[name] || isNaN(this.pForm[name]))
            {
                this.pForm[name] = parseInt(0);
            }

            if (parseInt(this.pForm[name]) > min)
            {
                this.pForm[name] = parseInt(this.pForm[name]) - 1;
            }
        },

        // increase value of +/- button in the admin via inputPlusminus
        increasePlusMinusValueAdmin(name, min=0, max=0)
        {
            this.touchForm();

            if (!this[name] || isNaN(this[name]))
            {
                this[name] = parseInt(0);
            }

            if (max == 0 || parseInt(this[name]) < max)
            {
                this[name] = parseInt(this[name]) + 1;
            }
        },

        // decrease value of +/- button in the admin via inputPlusminus
        decreasePlusMinusValueAdmin(name, min=0, max=0)
        {
            this.touchForm();

            if (!this[name] || isNaN(this[name]))
            {
                this[name] = parseInt(0);
            }

            if (parseInt(this[name]) > min)
            {
                this[name] = parseInt(this[name]) - 1;
            }
        },

        // check product option inventory
        checkProductOptionInventory(id)
        {
            this.axios.post('/product-option-inventory/'+id, {}).then(res => {
                if (res && res.data && res.data.success)
                {
                    this.productOptionTrackInventory = res.data.track_inventory ? true : false;
                    this.productOptionInventory = res.data.inventory;
                    this.productOptionInventoryLoaded = true;
                }
                else
                {
                    this.productOptionTrackInventory = false;
                    this.productOptionInventoryLoaded = false;
                }
            });
        },

        // show/hide password in password input boxes
        showHidePassword()
        {
            this.passwordInputVisible = !this.passwordInputVisible;
        },

        // create clickable links within text
        convertTextToClickableLinks(str)
        {
            var res, pattern1, pattern2, pattern3, pattern4;

            // match all http, https and ftp
            pattern1 = /(\b(https|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
            res = str.replace(pattern1, '<a class="link" href="$1" target="_blank">$1</a>');

            // match all www
            pattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim;
            res = res.replace(pattern2, '$1<a class="link" href="http://$2" target="_blank">$2</a>');

            // match all with just domain name and ending
            pattern3 = /(\s)([a-zA-Z0-9.-]+\.[a-zA-Z]{2,6})\b/gim;
            res = res.replace(pattern3, '$1<a class="link" href="http://$2" target="_blank">$2</a>');

            // match all email
            pattern4 = /(([a-zA-Z0-9\-\_\.])+@[a-zA-Z\_]+?(\.[a-zA-Z]{2,6})+)/gim;
            res = res.replace(pattern4, '<a class="link" href="mailto:$1">$1</a>');

            return res;
        },
    },
    mounted: function ()
    {
        // add event listener so that all internal links open in same tab
        try
        {
            addEventListener('click', function(event)
            {
                let el = event.target;
                if (el.tagName === 'A' && !el.isContentEditable && (!el.host || el.host == window.location.host))
                {
                    el.setAttribute('target', '');
                }
                else if ((el.parentElement && el.parentElement.tagName === 'A') && !el.parentElement.isContentEditable && (!el.parentElement.host || el.parentElement.host == window.location.host))
                {
                    el.parentElement.setAttribute('target', '');
                }
            }, true);
        }
        catch (e) {}

        // load keep in touch
        try
        {
            if (location.href.includes('/ponocms'))
            {
                this.axios.post('/ponocms/dashboard/keep-in-touch-count').then(res => {
                    if (res && res.data && res.data.success)
                    {
                        this.keepInTouchCount = res.data.count;
                    }
                });

                this.axios.post('/ponocms/dashboard/keep-in-touch-overdue-count').then(res => {
                    if (res && res.data && res.data.success)
                    {
                        this.keepInTouchOverdueCount = res.data.count;
                    }
                });
            }
        }
        catch (e) {}
    },
    created: function ()
    {
        this.modalOpen['modal-flash-error'] = true;
        this.modalOpen['modal-flash-success'] = true;

        // set dark mode
        if (localStorage.ponoTheme === 'light')
        {
            this.darkMode = 1;
        }
        else if (localStorage.ponoTheme === 'dark')
        {
            this.darkMode = 2;
        }
        else
        {
            this.darkMode = 3;
        }

        this.vueAppLoading = false;

        window.addEventListener('scroll', this.scrollListener);
    },
    destroyed: function ()
    {
        window.removeEventListener('scroll', this.scrollListener);
    },
});

// add Tailwind components
app.component('disclosure', Disclosure);
app.component('disclosurebutton', DisclosureButton);
app.component('disclosurepanel', DisclosurePanel);
app.component('hdialog', HDialog);
app.component('hdialogoverlay', HDialogOverlay);
app.component('hdialogtitle', HDialogTitle);
app.component('hdialogdescription', HDialogDescription);
app.component('listbox', Listbox);
app.component('listboxbutton', ListboxButton);
app.component('listboxlabel', ListboxLabel);
app.component('listboxoption', ListboxOption);
app.component('listboxoptions', ListboxOptions);
app.component('hmenu', HMenu);
app.component('hmenubutton', HMenuButton);
app.component('hmenuitems', HMenuItems);
app.component('hmenuitem', HMenuItem);
app.component('popover', Popover);
app.component('popoverbutton', PopoverButton);
app.component('popovergroup', PopoverGroup);
app.component('popoverpanel', PopoverPanel);
app.component('radiogroup', RadioGroup);
app.component('radiogroupdescription', RadioGroupDescription);
app.component('radiogrouplabel', RadioGroupLabel);
app.component('radiogroupoption', RadioGroupOption);
app.component('hswitch', HSwitch);
app.component('hswitchgroup', HSwitchGroup);
app.component('hswitchlabel', HSwitchLabel);
app.component('tabgroup', TabGroup);
app.component('tablist', TabList);
app.component('tab', Tab);
app.component('tabpanels', TabPanels);
app.component('tabpanel', TabPanel);
app.component('transitionroot', TransitionRoot);
app.component('transitionchild', TransitionChild);

// add other components
app.component('ref', ref);
app.component('giphy-js-sdk-core', GphApiClient);
app.component('litepie-datepicker', LitepieDatepicker);
app.component('quilleditor', QuillEditor)
app.component('vccarousel', VCCarousel)
app.component('vcslide', VCSlide)
app.component('vcpagination', VCPagination)
app.component('vcnavigation', VCNavigation)

app.use(flareVue);

// add ability for styles in Blade templates
app.component('vstyle',
{
    render: function ()
    {
        return h('style', this.$slots.default())
    }
});

// configs
app.config.devtools = false;
app.config.productionTip = false;
app.config.globalProperties.axios = axios;

// mount app
app.mount("#app");